import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import {
  DateInput,
  NumberInput,
  RadioButtonGroupInput,
  ReferenceInput,
  SelectInput,
  TextInput,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { ItsLookupInput } from "./ItsLookupInput";

const GENDER_CHOICES = [
  { id: "MALE", name: "Male" },
  { id: "FEMALE", name: "Female" },
];
const STATUS_CHOICES = [
  { id: "ACTIVE", name: "Active" },
  { id: "INACTIVE", name: "Inactive" },
];
const RELATION_CHOICES = [
  { id: "FATHER", name: "Father" },
  { id: "MOTHER", name: "Mother" },
  { id: "OTHER", name: "Other" },
];
const ADMISSION_MODE_CHOICES = [
  { id: "AUTO", name: "Automatic (new only for a student's first enrollment)" },
  { id: "NEW", name: "New admission (admission fee applies)" },
  { id: "RETURNING", name: "Returning student (no admission fee)" },
];

function Section({ title, hint }: { title: string; hint?: string }) {
  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="subtitle1">{title}</Typography>
      {hint && (
        <Typography variant="body2" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Box>
  );
}

/** Shared by the create and edit screens. Field rules mirror `validateStudentForm`. */
export function StudentFormFields({ isEdit }: { isEdit: boolean }) {
  const [g2Name, g2Relation, g2Phone] = useWatch({
    name: ["guardian2Name", "guardian2Relation", "guardian2Phone"],
  }) as Array<string | null | undefined>;
  const hasGuardian2 = [g2Name, g2Relation, g2Phone].every(
    (v) => v != null && String(v).trim() !== ""
  );

  return (
    <>
      <Typography variant="subtitle1">Student</Typography>
      <ItsLookupInput />
      <TextInput source="name" label="Name" isRequired fullWidth />
      <SelectInput
        source="gender"
        label="Gender"
        choices={GENDER_CHOICES}
        emptyText="Not set"
        fullWidth
      />
      <DateInput source="dateOfBirth" label="Date of birth" fullWidth />
      <TextInput source="address" label="Address" fullWidth multiline minRows={2} />
      <DateInput source="joinedOn" label="Joined on" isRequired fullWidth />
      {isEdit && <DateInput source="leftOn" label="Left on" fullWidth />}
      {isEdit && <SelectInput source="status" label="Status" choices={STATUS_CHOICES} fullWidth />}
      <TextInput source="notes" label="Notes" fullWidth multiline minRows={2} />

      {!isEdit && (
        <>
          <Section
            title="Enrollment (optional)"
            hint="Optionally enroll the student into a class upon creation."
          />
          <ReferenceInput
            source="academicYearId"
            reference="academicYears"
            filter={{ isClosed: false }}
          >
            <SelectInput
              optionText="name"
              label="Academic year"
              emptyText="Current year"
              fullWidth
            />
          </ReferenceInput>
          <ReferenceInput source="classId" reference="madrasaClasses" filter={{ isActive: true }}>
            <SelectInput
              optionText="name"
              label="Class"
              emptyText="None (do not enroll yet)"
              fullWidth
            />
          </ReferenceInput>
          <TextInput source="section" label="Section (e.g. A, B)" fullWidth />
          <NumberInput source="rollNo" label="Roll no (optional)" min={1} fullWidth />
          <DateInput
            source="enrolledOn"
            label="Enrolled on"
            helperText="Fees start from this month. Leave empty for today (kept inside the academic year)."
            fullWidth
          />
          <SelectInput
            source="admissionMode"
            label="Admission type"
            choices={ADMISSION_MODE_CHOICES}
            defaultValue="AUTO"
            fullWidth
          />
        </>
      )}

      <Section
        title="Guardian 1 (optional)"
        hint="The primary contact unless guardian 2 is chosen below. Auto-filled from ITS directory or student name if omitted."
      />
      <TextInput source="guardian1Name" label="Name" fullWidth />
      <SelectInput
        source="guardian1Relation"
        label="Relation"
        choices={RELATION_CHOICES}
        emptyText="Father (default)"
        fullWidth
      />
      <TextInput
        source="guardian1Phone"
        label="WhatsApp number"
        helperText="With country code, e.g. +919876543210."
        fullWidth
      />
      <TextInput source="guardian1ItsId" label="ITS ID (optional)" fullWidth />

      <Section
        title="Guardian 2 (optional)"
        hint="Add a second guardian with their own WhatsApp number. Name, relation and number are all needed."
      />
      <TextInput source="guardian2Name" label="Name" fullWidth />
      <SelectInput
        source="guardian2Relation"
        label="Relation"
        choices={RELATION_CHOICES}
        emptyText="Not set"
        fullWidth
      />
      <TextInput source="guardian2Phone" label="WhatsApp number" fullWidth />
      <TextInput source="guardian2ItsId" label="ITS ID (optional)" fullWidth />

      <Section
        title="Primary contact"
        hint="Whose name and WhatsApp number the office sees as the contact for this student."
      />
      <RadioButtonGroupInput
        source="primaryGuardian"
        label={false}
        defaultValue={1}
        parse={(value: string | number) => Number(value)}
        choices={[
          { id: 1, name: "Guardian 1" },
          { id: 2, name: "Guardian 2", disabled: !hasGuardian2 },
        ]}
      />
    </>
  );
}
